import { GlassCard } from '@/components/design/GlassCard';
import { NeonButton } from '@/components/design/NeonButton';
import { ArrowLeft, Save, Image as ImageIcon, Eye, X } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { AdminAPI, API_URL } from '@/services/api';
import { CustomSelect } from '@/components/ui/CustomSelect';
import { toast } from 'sonner';
import { slugify } from '@/utils/slug';
import { apiErrorMessage } from '@/utils/apiError';
import { RichTextEditor } from '@/components/editor/RichTextEditor';
import { isHtmlEmpty } from '@/utils/html';

export const BlogEditorPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState<'en' | 'az'>('en');
  const [activeLangs, setActiveLangs] = useState({ en: true, az: true });
  const [admins, setAdmins] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState({
    titleEn: '',
    contentEn: '',
    titleAz: '',
    contentAz: '',
    slug: '',
    authorId: '',
    categoryId: '',
    published: false,
    coverImage: '',
  });
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  /**
   * Slug əl ilə yazılıbsa başlıqdan avtomatik doldurma dayanır — redaktorun
   * yazdığı ünvanı üstündən silmirik.
   */
  const [slugTouched, setSlugTouched] = useState(false);

  /** Başlıq dəyişəndə slug-ı da yeniləyir (yalnız hələ əl ilə toxunulmayıbsa). */
  const applyTitle = (value: string, target: 'titleEn' | 'titleAz') => {
    setFormData(prev => {
      const next = { ...prev, [target]: value };
      if (!slugTouched) {
        // Azərbaycanca başlıq üstündür, yoxdursa ingiliscəyə düşürük
        const source = target === 'titleAz' ? value : (prev.titleAz || value);
        next.slug = slugify(source);
      }
      return next;
    });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Create local preview
    const previewUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setFormData(prev => ({ ...prev, coverImage: previewUrl }));
  };

  // Fetch admins and set default author (and post if id exists)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [adminsRes, profileRes, categoriesRes] = await Promise.all([
          AdminAPI.auth.getAdmins(),
          AdminAPI.auth.getProfile(),
          AdminAPI.categories.getAll()
        ]);
        setAdmins(adminsRes.data);
        setCategories(categoriesRes.data);
        
        if (id) {
            // Edit mode: fetch post
            const postRes = await AdminAPI.content.getPost(id);
            const post = postRes.data;
            setFormData({
                titleEn: post.titleEn || '',
                contentEn: post.contentEn || '',
                titleAz: post.titleAz || '',
                contentAz: post.contentAz || '',
                slug: post.slug || '',
                authorId: post.authorId,
                categoryId: post.categoryId || '',
                published: post.published,
                coverImage: post.coverImage || '',
            });
            // Mövcud yazının ünvanı artıq paylaşılıb — başlıq dəyişəndə slug-a toxunmuruq
            setSlugTouched(true);
            // Determine active langs based on content existence
            setActiveLangs({
                en: !!(post.titleEn || post.contentEn),
                az: !!(post.titleAz || post.contentAz),
            });
            // Set lang view to the one that has content
            if (post.titleEn || post.contentEn) setLang('en');
            else if (post.titleAz || post.contentAz) setLang('az');

        } else if (!formData.authorId) {
             // Create mode: set default author
            setFormData(prev => ({ ...prev, authorId: profileRes.data.id }));
        }
      } catch (err) {
        console.error("Failed to fetch initial data", err);
        toast.error("Failed to load data");
      }
    };
    fetchData();
  }, [id]); // Depend on id

  const handleSave = async (published: boolean) => {
    try {
      setLoading(true);
      
      // Validation based on active languages
      if (!activeLangs.en && !activeLangs.az) {
        toast.error('Please select at least one language');
        setLoading(false); 
        return;
      }

      // HTML redaktoru boş olanda `<br>` qaytara bilir — sadə boşluq yoxlaması bəs etmir
      if (activeLangs.en && (!formData.titleEn || isHtmlEmpty(formData.contentEn))) {
        toast.error('English title and content are required');
        setLoading(false);
        return;
      }
      if (activeLangs.az && (!formData.titleAz || isHtmlEmpty(formData.contentAz))) {
        toast.error('Azerbaijani title and content are required');
        setLoading(false);
        return;
      }

      setLoading(true);

      let coverImageUrl = formData.coverImage;

      // Upload image if selected
      if (selectedFile) {
        try {
          const res = await AdminAPI.uploads.uploadImage(selectedFile);
          // ⚠️ Ünvan SABİT YAZILMIR — bax `services/api.ts`.
          // Əvvəl burada `https://api.politicalmaze.com` sabit idi: lokalda
          // işləyəndə şəkil canlı serverə yüklənirdi, yəni inkişaf zamanı
          // production-a data yazılırdı.
          // `/api/v1` sonluğu atılır, çünki `res.data.url` onsuz da tam yoldur.
          coverImageUrl = `${API_URL.replace(/\/api\/v1\/?$/, '')}${res.data.url}`;
        } catch (uploadError) {
           console.error("Image upload failed", uploadError);
           toast.error("Failed to upload image");
           setLoading(false);
           return;
        }
      }

      // Slug boş qalıbsa serverin başlıqdan yaratmasına buraxırıq
      const requestedSlug = slugify(formData.slug) || slugify(formData.titleAz || formData.titleEn);

      // Prepare payload - send undefined/null for disabled languages to clear them if needed
      const payload = {
        ...formData,
        slug: requestedSlug,
        titleEn: activeLangs.en ? formData.titleEn : undefined,
        contentEn: activeLangs.en ? formData.contentEn : undefined,
        titleAz: activeLangs.az ? formData.titleAz : undefined,
        contentAz: activeLangs.az ? formData.contentAz : undefined,
        categoryId: formData.categoryId || null,
        coverImage: coverImageUrl,
        published,
      };

      const { data: saved } = id
        ? await AdminAPI.content.updatePost(id, payload)
        : await AdminAPI.content.createPost(payload);

      toast.success(published ? 'Post published!' : 'Draft saved!');

      // Server slug-ı təkrar olduğu üçün dəyişibsə, redaktor bunu bilməlidir
      if (saved?.slug && saved.slug !== requestedSlug) {
        setFormData(prev => ({ ...prev, slug: saved.slug }));
        toast.info(`«${requestedSlug}» artıq mövcud idi — ünvan «${saved.slug}» kimi saxlanıldı.`);
      }

      navigate('/blog');
    } catch (error) {
       console.error(error);
       toast.error(apiErrorMessage(error, 'Failed to save post'));
    } finally {
      setLoading(false);
    }
  };

  const toggleActiveLang = (l: 'en' | 'az') => {
    setActiveLangs(prev => {
        const next = { ...prev, [l]: !prev[l] };
        // Don't allow turning off both
        if (!next.en && !next.az) return prev;
        // If we turn on a lang, switch view to it
        if (next[l] && !prev[l]) setLang(l);
        return next;
    })
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col gap-6 relative">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/blog">
            <NeonButton variant="ghost" size="sm" className="rounded-full w-10 h-10 p-0">
              <ArrowLeft className="w-5 h-5" />
            </NeonButton>
          </Link>
          
          {/* Language Toggle & Status */}
          <div className="flex items-center gap-2 bg-background/50 rounded-lg p-1 border border-white/10">
            <div className="flex items-center mr-2 px-2 border-r border-white/10">
                 <label className="flex items-center gap-1 cursor-pointer mr-2">
                    <input type="checkbox" checked={activeLangs.en} onChange={() => toggleActiveLang('en')} className="accent-primary"/>
                    <span className={`text-xs font-bold ${activeLangs.en ? 'text-foreground' : 'text-muted-foreground'}`}>EN</span>
                 </label>
                 <label className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={activeLangs.az} onChange={() => toggleActiveLang('az')} className="accent-primary"/>
                    <span className={`text-xs font-bold ${activeLangs.az ? 'text-foreground' : 'text-muted-foreground'}`}>AZ</span>
                 </label>
            </div>

            <button
              onClick={() => activeLangs.en && setLang('en')}
              disabled={!activeLangs.en}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                lang === 'en' 
                    ? 'bg-primary text-black' 
                    : activeLangs.en ? 'text-muted-foreground hover:text-white' : 'text-muted-foreground/30 cursor-not-allowed'
              }`}
            >
              Edit EN
            </button>
            <button
              onClick={() => activeLangs.az && setLang('az')}
              disabled={!activeLangs.az}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                lang === 'az' 
                    ? 'bg-primary text-black' 
                    : activeLangs.az ? 'text-muted-foreground hover:text-white' : 'text-muted-foreground/30 cursor-not-allowed'
              }`}
            >
              Edit AZ
            </button>
          </div>

          <div className="flex-1 min-w-[300px]">
            <input 
              type="text" 
              placeholder={
                  lang === 'en' 
                    ? (activeLangs.en ? "Post Title (English)..." : "English Disabled")
                    : (activeLangs.az ? "Post Title (Azerbaijani)..." : "Azerbaijani Disabled")
              }
              value={lang === 'en' ? formData.titleEn : formData.titleAz}
              onChange={(e) => applyTitle(e.target.value, lang === 'en' ? 'titleEn' : 'titleAz')}
              disabled={lang === 'en' ? !activeLangs.en : !activeLangs.az}
              className="bg-transparent text-3xl font-bold focus:outline-none placeholder:text-muted-foreground/50 w-full disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <NeonButton variant="ghost" className="text-muted-foreground hover:text-white" onClick={() => setShowPreview(true)}>
             <Eye className="mr-2 h-4 w-4" /> Preview
          </NeonButton>
          <NeonButton variant="secondary" onClick={() => handleSave(false)} disabled={loading}>
            Save Draft
          </NeonButton>
          <NeonButton onClick={() => handleSave(true)} disabled={loading}>
            <Save className="mr-2 h-4 w-4" /> {id ? 'Update' : 'Publish'}
          </NeonButton>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-0">
        <div className="lg:col-span-2 flex flex-col gap-4">
          {/* Editor — sahtə alətlər paneli əvəzinə işlək redaktor */}
          <GlassCard className="flex-1 p-0 overflow-hidden relative flex flex-col min-h-0">
             <div className="absolute top-2 right-2 z-10 px-2 py-1 bg-primary/20 text-primary text-xs rounded border border-primary/20 pointer-events-none">
                Editing: {lang === 'en' ? 'English' : 'Azerbaijani'}
             </div>
            {/*
              `key` dil dəyişəndə redaktoru yenidən qurur — əks halda
              köhnə dilin mətni ekranda qalırdı.
            */}
            <RichTextEditor
              key={lang}
              value={lang === 'en' ? formData.contentEn : formData.contentAz}
              onChange={(html) => {
                setFormData(prev => lang === 'en' ? { ...prev, contentEn: html } : { ...prev, contentAz: html });
              }}
              disabled={lang === 'en' ? !activeLangs.en : !activeLangs.az}
              placeholder={
                  lang === 'en'
                    ? (activeLangs.en ? "Write your story here (English)..." : "English Disabled")
                    : (activeLangs.az ? "Mətni buraya yazın (Azərbaycanca)..." : "Azerbaijani Disabled")
              }
            />
          </GlassCard>
        </div>

        <div className="space-y-6 overflow-y-auto">
          <GlassCard>
            <h3 className="font-semibold mb-4">Publishing Settings</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase">Slug</label>
                <input
                  className="w-full bg-background/50 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  placeholder="url-slug"
                  value={formData.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setFormData(prev => ({ ...prev, slug: e.target.value }));
                  }}
                  onBlur={(e) => {
                    // yazılanı saxlanacaq formaya salırıq ki, panel real ünvanı göstərsin
                    const normalized = slugify(e.target.value);
                    if (normalized !== e.target.value) {
                      setFormData(prev => ({ ...prev, slug: normalized }));
                    }
                  }}
                />
                <p className="text-[11px] text-muted-foreground">
                  Başlıqdan avtomatik yaranır. Eyni slug varsa server sonuna nömrə artırır.
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase">Category</label>
                <CustomSelect
                  options={[
                    { value: "", label: "Uncategorized" },
                    ...categories.map(cat => ({ value: cat.id, label: cat.name }))
                  ]}
                  value={formData.categoryId}
                  onChange={(value) => setFormData(prev => ({ ...prev, categoryId: value }))}
                  placeholder="Select Category"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase">Author</label>
                <CustomSelect
                  options={admins.map(admin => ({ value: admin.id, label: admin.name || admin.email }))}
                  value={formData.authorId}
                  onChange={(value) => setFormData(prev => ({ ...prev, authorId: value }))}
                  placeholder="Select Author"
                />
              </div>
            </div>
          </GlassCard>

          <GlassCard className="border-dashed border-2 border-white/10 bg-transparent hover:bg-white/5 cursor-pointer transition-colors flex flex-col items-center justify-center py-8 relative overflow-hidden group">
            <input 
              type="file" 
              accept="image/*"
              className="absolute inset-0 opacity-0 cursor-pointer z-10"
              onChange={handleImageUpload}
              disabled={loading} // Only disable during save
            />
            
            {formData.coverImage ? (
                <div className="absolute inset-0 w-full h-full">
                    <img src={formData.coverImage} alt="Cover" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white text-xs font-bold">Change Image</span>
                    </div>
                </div>
            ) : (
                <>
                    <ImageIcon className="w-8 h-8 text-muted-foreground mb-2" />
                    <span className="text-sm text-muted-foreground">Upload Cover Image</span>
                </>
            )}
          </GlassCard>
        </div>
      </div>

        {/* Preview Modal */}
        {showPreview && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                <GlassCard className="w-full max-w-4xl h-[85vh] flex flex-col p-0 overflow-hidden text-foreground">
                    <div className="flex items-center justify-between p-4 border-b border-white/10 bg-white/5">
                        <h2 className="font-bold">Post Preview ({lang.toUpperCase()})</h2>
                        <button onClick={() => setShowPreview(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-8 bg-zinc-950">
                        {/* Simulated Client View */}
                        <div className="max-w-3xl mx-auto space-y-8">
                             {formData.coverImage && (
                                <div className="aspect-[21/9] w-full rounded-2xl overflow-hidden mb-8">
                                    <img src={formData.coverImage} className="w-full h-full object-cover" alt="Preview Cover" />
                                </div>
                             )}
                            <h1 className="text-5xl font-bold leading-tight">
                                {lang === 'en' ? formData.titleEn : formData.titleAz || 'Untitled Post'}
                            </h1>
                            {/*
                              Redaktor HTML qaytarır — önizləmə də HTML göstərməlidir,
                              yoxsa redaktor teqləri mətn kimi görünürdü.
                              Bura yalnız redaktorun öz mətnidir; saytda göstərilən
                              variant serverdə təmizlənir (`sanitize.util.ts`).
                            */}
                            <div
                              className="rte-content text-lg"
                              dangerouslySetInnerHTML={{ __html: lang === 'en' ? formData.contentEn : formData.contentAz }}
                            />
                        </div>
                    </div>
                </GlassCard>
            </div>
        )}
    </div>
  );
};
